import React from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, FileText, Calendar, CheckCircle, AlertTriangle, Download } from 'lucide-react';

const ESGReporting = () => {
  const esgMetrics = [
    { category: 'Environmental', score: 85, trend: '+5%', status: 'good' },
    { category: 'Social', score: 78, trend: '+2%', status: 'good' },
    { category: 'Governance', score: 92, trend: '+8%', status: 'excellent' },
  ];

  const reports = [
    { title: 'Q4 2024 ESG Report', date: '2024-12-31', status: 'published', size: '2.4 MB' },
    { title: 'Annual Sustainability Report 2024', date: '2024-12-15', status: 'published', size: '5.1 MB' },
    { title: 'Q3 2024 ESG Report', date: '2024-09-30', status: 'published', size: '2.2 MB' },
    { title: 'Q2 2024 ESG Report', date: '2024-06-30', status: 'published', size: '2.1 MB' },
  ];

  const upcomingReports = [
    { title: 'Q1 2025 ESG Report', dueDate: '2025-03-31', progress: 65 },
    { title: 'Mid-Year Sustainability Update', dueDate: '2025-06-30', progress: 20 },
  ];

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">ESG Reporting Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Monitor, track, and generate comprehensive ESG reports for stakeholders
          </p>
        </div>

        {/* ESG Score Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {esgMetrics.map((metric) => (
            <Card key={metric.category}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{metric.category}</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metric.score}</div>
                <div className="flex items-center space-x-2 mt-2">
                  <Progress value={metric.score} className="flex-1" />
                  <Badge variant={metric.status === 'excellent' ? 'default' : 'secondary'}>
                    {metric.trend}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="reports" className="space-y-6">
          <TabsList>
            <TabsTrigger value="reports">Published Reports</TabsTrigger>
            <TabsTrigger value="upcoming">Upcoming Reports</TabsTrigger>
            <TabsTrigger value="metrics">ESG Metrics</TabsTrigger>
          </TabsList>

          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <CardTitle>Published ESG Reports</CardTitle>
                <CardDescription>
                  Access and download your organization's ESG reports
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {reports.map((report, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        <FileText className="h-8 w-8 text-primary" />
                        <div>
                          <h3 className="font-semibold">{report.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            Published: {new Date(report.date).toLocaleDateString()} • {report.size}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Published
                        </Badge>
                        <Button variant="outline" size="sm">
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="upcoming">
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Reports</CardTitle>
                <CardDescription>
                  Track progress on reports currently in development
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {upcomingReports.map((report, index) => (
                    <div key={index} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold">{report.title}</h3>
                        <Badge variant="outline">
                          <Calendar className="w-3 h-3 mr-1" />
                          Due: {new Date(report.dueDate).toLocaleDateString()}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span>{report.progress}%</span>
                        </div>
                        <Progress value={report.progress} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="metrics">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Environmental Metrics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span>Carbon Footprint Reduction</span>
                    <Badge>-15% YoY</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Renewable Energy Usage</span>
                    <Badge>65%</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Waste Reduction</span>
                    <Badge>-22% YoY</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Water Conservation</span>
                    <Badge>-8% YoY</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Social & Governance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span>Employee Satisfaction</span>
                    <Badge>87%</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Board Diversity</span>
                    <Badge>45%</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Supply Chain Compliance</span>
                    <Badge>98%</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Training Hours per Employee</span>
                    <Badge>42h</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default ESGReporting;